import { useState, useEffect } from "react";
import { Card, CardContent } from "../ui/card";
import { Badge } from "../ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Calendar, Clock, User, Book, FlaskConical, Loader2 } from "lucide-react";
import API from "../api/api01";
import { toast } from "sonner";

interface ClassData {
  id: string;
  courseName: string;
  courseCode: string;
  teacherName: string;
  lectureType: "Theory" | "Practical";
  time: string;
  room: string;
  status: "P" | "A" | "L";
}

export function Timetable() {
  const [activeDay, setActiveDay] = useState("");
  const [timetableData, setTimetableData] = useState<Record<string, ClassData[]>>({});
  const [loading, setLoading] = useState(true);

  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const studentId = user.id;

  useEffect(() => {
    if (studentId) {
      fetchTimetable();
    }
  }, [studentId]);

  const fetchTimetable = async () => {
    setLoading(true);
    try {
      const response = await API.get(`/superadmin/student/timetable/${studentId}`);
      // Response is array of {day, slots}
      const mappedData: Record<string, ClassData[]> = {};

      response.data.forEach((item: any) => {
        const dayKey = item.day.toLowerCase();
        mappedData[dayKey] = item.slots.map((slot: any) => ({
          id: slot.id,
          courseName: slot.course,
          courseCode: slot.courseCode || "",
          teacherName: slot.teacherName || "Unknown",
          lectureType: slot.lectureType || "Theory",
          time: slot.time,
          room: slot.room || "N/A",
          status: slot.status === "present" ? "P" : slot.status === "absent" ? "A" : "L"
        }));
      });

      setTimetableData(mappedData);

      // Set active day to today if possible
      const today = new Date().toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
      if (mappedData[today]) {
        setActiveDay(today);
      } else {
        // Find first day with classes or default to monday
        const days = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
        const firstDayWithClasses = days.find(d => mappedData[d] && mappedData[d].length > 0);
        setActiveDay(firstDayWithClasses || "monday");
      }

    } catch (error) {
      console.error("Error fetching timetable:", error);
      toast.error("Failed to load timetable");
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: "P" | "A" | "L") => {
    const styles = {
      P: "bg-emerald-100 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800/50",
      A: "bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-400 border-red-200 dark:border-red-800/50",
      L: "bg-amber-100 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800/50"
    };

    const labels = {
      P: "Present",
      A: "Absent",
      L: "Leave"
    };

    return (
      <Badge className={`${styles[status]} border`} variant="outline">
        {labels[status]}
      </Badge>
    );
  };

  const getInitials = (name: string) => {
    return name.split(" ").map(n => n[0]).join("").toUpperCase();
  };

  const getDayLabel = (day: string) => {
    const labels: Record<string, string> = {
      monday: "Monday",
      tuesday: "Tuesday",
      wednesday: "Wednesday",
      thursday: "Thursday",
      friday: "Friday",
      saturday: "Saturday",
    };
    return labels[day] || day;
  };

  const ClassCard = ({ classData }: { classData: ClassData }) => (
    <Card className="overflow-hidden hover:shadow-lg hover:border-primary/30 transition-all duration-300 border-border/50 bg-card">
      <CardContent className="p-5">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-semibold text-foreground">{classData.courseName}</h3>
            </div>
            <p className="text-sm text-muted-foreground">{classData.courseCode}</p>
          </div>
          {getStatusBadge(classData.status)}
        </div>

        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <Avatar className="h-10 w-10 border border-border/50">
              <AvatarImage src={`https://api.dicebear.com/7.x/initials/svg?seed=${classData.teacherName}`} />
              <AvatarFallback>{getInitials(classData.teacherName)}</AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <User className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                <p className="text-sm font-medium truncate">{classData.teacherName}</p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="flex items-center gap-2">
              <Clock className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
              <span className="text-sm text-muted-foreground truncate">{classData.time}</span>
            </div>
            <div className="flex items-center gap-2">
              {classData.lectureType === "Theory" ? (
                <Book className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
              ) : (
                <FlaskConical className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
              )}
              <span className="text-sm text-muted-foreground">{classData.lectureType}</span>
            </div>
          </div>

          <div className="flex items-center justify-between pt-2 border-t border-border/50">
            <span className="text-xs text-muted-foreground">Room: {classData.room}</span>
            <Badge variant="outline" className="text-xs bg-accent/30 border-border/50">
              {classData.lectureType === "Practical" ? "2 hrs" : "1.5 hrs"}
            </Badge>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const days = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center">
          <Calendar className="h-5 w-5 text-primary" />
        </div>
        <div>
          <h1>Class Timetable</h1>
          <p className="text-muted-foreground text-sm mt-0.5">View your weekly class schedule</p>
        </div>
      </div>

      <Tabs value={activeDay} onValueChange={setActiveDay} className="space-y-6">
        <TabsList className="grid w-full grid-cols-3 md:grid-cols-6 bg-muted/50 p-1">
          {days.map((day) => (
            <TabsTrigger
              key={day}
              value={day}
              className="data-[state=active]:bg-primary data-[state=active]:text-white dark:data-[state=active]:text-foreground capitalize"
            >
              {day.substring(0, 3)}
            </TabsTrigger>
          ))}
        </TabsList>


        {days.map((day) => (
          <TabsContent key={day} value={day} className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-semibold text-lg">{getDayLabel(day)}</h2>
              <Badge variant="outline" className="bg-primary/5 border-primary/20 text-primary">
                {timetableData[day]?.length || 0} {(timetableData[day]?.length || 0) === 1 ? "Class" : "Classes"}
              </Badge>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {timetableData[day]?.map((classData) => (
                <ClassCard key={classData.id} classData={classData} />
              ))}
            </div>

            {(!timetableData[day] || timetableData[day].length === 0) && (
              <Card className="border-dashed border-2">
                <CardContent className="flex flex-col items-center justify-center py-12 text-center">
                  <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center mb-4">
                    <Calendar className="h-6 w-6 text-muted-foreground" />
                  </div>
                  <h3 className="text-lg font-medium">No Classes Assigned Yet</h3>
                  <p className="text-muted-foreground max-w-xs mx-auto mt-1">
                    Your timetable has not been assigned for {getDayLabel(day)} yet. Please check back later.
                  </p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
